import { Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button, ModalFrame } from '@krp/ui-kit'

import { memberCandidates } from '../model/select'
import type { CashierPerson, ShownError } from '../model/types'
import { ErrorBlock } from './parts'
import './schedule.css'

export interface AddMembersDialogProps {
  /** Кого можно добавить: справочник без архивных. */
  people: readonly CashierPerson[]
  /** Кто уже в составе объекта: в списке не показан. */
  memberIds: readonly string[]
  /** Недавно добавленные, свежий первым: помечены «недавно» и подняты наверх. */
  recent: readonly string[]
  /** Добавляет одного кассира в состав; `null` — добавлен, иначе отказ службы. */
  onAdd: (cashierId: string) => Promise<ShownError | null>
  onClose: () => void
}

/**
 * Небольшое окно «Добавить сотрудника»: поиск и под ним список всех кассиров, которых ещё нет в составе (с прокруткой); строка с
 * галочкой, нажатие на строку отмечает и снимает, отметить можно нескольких. «Добавить (N)» добавляет отмеченных по одному в
 * порядке списка; на первом отказе останавливается и показывает его дословно в окне, а уже добавленные снимает с отметки и больше
 * не добавляет при повторе. Когда добавлены все, окно закрывается. Esc и «Отмена» закрывают его без добавления оставшихся.
 */
export function AddMembersDialog({ people, memberIds, recent, onAdd, onClose }: AddMembersDialogProps) {
  const search = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [checked, setChecked] = useState<ReadonlySet<string>>(() => new Set())
  const [added, setAdded] = useState<readonly string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<ShownError | null>(null)

  useEffect(() => {
    search.current?.focus()
  }, [])

  const candidates = memberCandidates(people, [...memberIds, ...added], recent, query)
  // Отмеченные, которых сейчас не видно из-за поиска, тоже добавляются: считаем по всему справочнику, а не по видимой части.
  const everyChecked = memberCandidates(people, [...memberIds, ...added], recent, '')
    .map((item) => item.person)
    .filter((person) => checked.has(person.id.toLowerCase()))
  const count = everyChecked.length

  const toggle = (cashierId: string) =>
    setChecked((current) => {
      const next = new Set(current)
      const key = cashierId.toLowerCase()
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const submit = async () => {
    setBusy(true)
    setError(null)
    const done: string[] = []
    for (const person of everyChecked) {
      const failure = await onAdd(person.id)
      if (failure !== null) {
        setError(failure)
        break
      }
      done.push(person.id)
    }
    setBusy(false)
    if (done.length === everyChecked.length) {
      onClose()
      return
    }
    const doneKeys = new Set(done.map((id) => id.toLowerCase()))
    setAdded((current) => [...current, ...done])
    setChecked((current) => new Set([...current].filter((key) => !doneKeys.has(key))))
  }

  return (
    <ModalFrame title="Добавить сотрудника" size="sm" covered={false} {...(busy ? {} : { onClose })}>
      <div className="sch-adddlg">
        <label className="sch-select__field">
          <input
            ref={search}
            type="text"
            className="sch-select__input"
            aria-label="Найти кассира"
            placeholder="Найти кассира"
            autoComplete="off"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Search size={16} aria-hidden="true" className="sch-select__icon" />
        </label>
        <ul className="sch-adddlg__list" aria-label="Кассиры, которых можно добавить">
          {candidates.map(({ person, recent: isRecent }) => (
            <li key={person.id}>
              <label className="sch-adddlg__row">
                <input type="checkbox" checked={checked.has(person.id.toLowerCase())} disabled={busy} onChange={() => toggle(person.id)} />
                <span className="sch-adddlg__name">{person.name}</span>
                {isRecent && <span className="sch-option__recent">недавно</span>}
              </label>
            </li>
          ))}
        </ul>
        {candidates.length === 0 && (
          <p className="sch-select__status" role="status">
            {query.trim() === '' ? 'Все кассиры справочника уже в составе объекта.' : 'Никого не нашли: проверьте написание имени.'}
          </p>
        )}
        {error !== null && <ErrorBlock error={error} />}
        <div className="sch-editor__buttons">
          <Button
            variant="primary"
            actionId="schedule.place.add-members-confirm"
            busy={busy}
            disabled={count === 0}
            disabledReason={count === 0 ? 'Отметьте хотя бы одного кассира в списке.' : null}
            icon={busy ? <span className="sch-spin" aria-hidden="true" /> : undefined}
            onClick={() => void submit()}
          >
            {`Добавить (${count})`}
          </Button>
          <Button variant="ghost" actionId="schedule.place.add-members-cancel" disabled={busy} onClick={onClose}>
            Отмена
          </Button>
        </div>
      </div>
    </ModalFrame>
  )
}
